import { useEffect, useState } from "react";
import { FileArchive, Send, Award, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

/**
 * Two portfolio-generation actions:
 *   1. Letter of Intent — Florida §1002.41 notice to the county superintendent
 *   2. Report Card — Claude-generated narrative + letter grades over a date range
 *
 * Both open a small dialog to collect the extra inputs, then stream the PDF back.
 */
export default function PortfolioActions({ student }) {
  const [loiOpen, setLoiOpen] = useState(false);
  const [rcOpen, setRcOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [counties, setCounties] = useState([]);
  const [profile, setProfile] = useState({});

  const [loi, setLoi] = useState({
    county: "", parent_full_name: "", address: "", city: "", state: "FL", zip: "", phone: "",
    effective_date: new Date().toISOString().slice(0, 10),
  });
  const [rc, setRc] = useState({
    start_date: `${new Date().getFullYear()}-01-01`,
    end_date: `${new Date().getFullYear()}-12-31`,
    tone: "warm",
  });

  useEffect(() => {
    (async () => {
      try {
        const [c, me] = await Promise.all([
          api.get("/portfolio/counties"),
          api.get("/auth/me"),
        ]);
        setCounties(c.data.counties || []);
        setProfile(me.data);
        setLoi((p) => ({
          ...p,
          county: me.data.county || "",
          parent_full_name: me.data.parent_full_name || me.data.name || "",
          address: me.data.address || "",
          city: me.data.city || "",
          state: me.data.state || "FL",
          zip: me.data.zip || "",
          phone: me.data.phone || "",
        }));
      } catch (_e) { /* ignore */ }
    })();
  }, []);

  const streamPdf = async (path, body, fallbackName) => {
    setBusy(true);
    try {
      const resp = await api.post(path, body, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
      return true;
    } catch (e) {
      // For blob responses, error body is also a blob — parse it out
      let detail = e.message;
      const errBlob = e.response?.data;
      if (errBlob && errBlob instanceof Blob) {
        try { const txt = await errBlob.text(); detail = (JSON.parse(txt).detail || txt); } catch (_e2) { /* keep default */ }
      }
      toast.error(detail || `Could not generate ${fallbackName}`);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const generateLoi = async () => {
    if (!student?.date_of_birth) {
      toast.error(`${student.name} needs a date of birth on their profile first.`);
      return;
    }
    if (!loi.county) { toast.error("Pick a Florida county"); return; }
    if (!loi.parent_full_name.trim()) { toast.error("Add your full name"); return; }
    const ok = await streamPdf("/portfolio/letter-of-intent", {
      student_ids: [student.id],
      county: loi.county,
      parent_full_name: loi.parent_full_name,
      address: loi.address,
      city: loi.city,
      state: loi.state,
      zip: loi.zip,
      phone: loi.phone,
      effective_date: loi.effective_date,
    }, "letter of intent");
    if (ok) { toast.success("Letter of Intent ready — sign and send within 30 days"); setLoiOpen(false); }
  };

  const generateReportCard = async () => {
    const ok = await streamPdf("/portfolio/report-card", {
      student_id: student.id,
      start_date: rc.start_date,
      end_date: rc.end_date,
      tone: rc.tone,
    }, "report card");
    if (ok) { toast.success("Report card ready"); setRcOpen(false); }
  };

  return (
    <>
      <Button
        variant="outline"
        className="rounded-full border-2 border-blue-200 text-blue-700 hover:bg-blue-50"
        onClick={() => setLoiOpen(true)}
        data-testid="profile-loi-btn"
      >
        <Send className="w-4 h-4 mr-2" /> Letter of Intent
      </Button>
      <Button
        variant="outline"
        className="rounded-full border-2 border-violet-200 text-violet-700 hover:bg-violet-50"
        onClick={() => setRcOpen(true)}
        data-testid="profile-report-card-btn"
      >
        <Award className="w-4 h-4 mr-2" /> Report card
      </Button>

      {/* Letter of Intent dialog */}
      <Dialog open={loiOpen} onOpenChange={setLoiOpen}>
        <DialogContent className="max-w-lg rounded-2xl" data-testid="loi-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Letter of Intent · Florida §1002.41</DialogTitle>
            <DialogDescription>File this notice with your county superintendent within 30 days of starting home education.</DialogDescription>
          </DialogHeader>
          {!student?.date_of_birth && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm p-3">
              Add {student.name}&apos;s date of birth on their profile before generating this letter.
            </div>
          )}
          <div className="space-y-4">
            <div>
              <Label>County</Label>
              <Select value={loi.county} onValueChange={(v) => setLoi({ ...loi, county: v })}>
                <SelectTrigger className="mt-2 rounded-xl h-11" data-testid="loi-county"><SelectValue placeholder="Pick your county" /></SelectTrigger>
                <SelectContent className="max-h-64">{counties.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Your full name</Label>
                <Input value={loi.parent_full_name} onChange={(e) => setLoi({ ...loi, parent_full_name: e.target.value })} className="mt-2 rounded-xl h-11" data-testid="loi-parent-name" />
              </div>
              <div>
                <Label>Effective date</Label>
                <Input type="date" value={loi.effective_date} onChange={(e) => setLoi({ ...loi, effective_date: e.target.value })} className="mt-2 rounded-xl h-11" data-testid="loi-effective-date" />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Input value={loi.address} onChange={(e) => setLoi({ ...loi, address: e.target.value })} placeholder="123 Palm Ave" className="mt-2 rounded-xl h-11" data-testid="loi-address" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>City</Label>
                <Input value={loi.city} onChange={(e) => setLoi({ ...loi, city: e.target.value })} className="mt-2 rounded-xl h-11" data-testid="loi-city" />
              </div>
              <div>
                <Label>State</Label>
                <Input value={loi.state} onChange={(e) => setLoi({ ...loi, state: e.target.value })} className="mt-2 rounded-xl h-11" data-testid="loi-state" />
              </div>
              <div>
                <Label>ZIP</Label>
                <Input value={loi.zip} onChange={(e) => setLoi({ ...loi, zip: e.target.value })} className="mt-2 rounded-xl h-11" data-testid="loi-zip" />
              </div>
            </div>
            <div>
              <Label>Phone (optional)</Label>
              <Input value={loi.phone} onChange={(e) => setLoi({ ...loi, phone: e.target.value })} className="mt-2 rounded-xl h-11" data-testid="loi-phone" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLoiOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={generateLoi} disabled={busy || !student?.date_of_birth} className="rounded-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="loi-generate">
              {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Building…</>) : (<><FileArchive className="w-4 h-4 mr-2" />Generate PDF</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Card dialog */}
      <Dialog open={rcOpen} onOpenChange={setRcOpen}>
        <DialogContent className="max-w-lg rounded-2xl" data-testid="rc-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Report Card</DialogTitle>
            <DialogDescription>We'll write a narrative and assign per-subject letter grades from {student.name}&apos;s proficiency scores and lesson completion.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>From</Label>
                <Input type="date" value={rc.start_date} onChange={(e) => setRc({ ...rc, start_date: e.target.value })} className="mt-2 rounded-xl h-11" data-testid="rc-start-date" />
              </div>
              <div>
                <Label>To</Label>
                <Input type="date" value={rc.end_date} onChange={(e) => setRc({ ...rc, end_date: e.target.value })} className="mt-2 rounded-xl h-11" data-testid="rc-end-date" />
              </div>
            </div>
            <div>
              <Label>Narrative tone</Label>
              <Select value={rc.tone} onValueChange={(v) => setRc({ ...rc, tone: v })}>
                <SelectTrigger className="mt-2 rounded-xl h-11" data-testid="rc-tone"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="warm">Warm — parent-friendly, encouraging</SelectItem>
                  <SelectItem value="formal">Formal — district-style, objective</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRcOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={generateReportCard} disabled={busy} className="rounded-full bg-violet-600 hover:bg-violet-700 text-white" data-testid="rc-generate">
              {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Writing…</>) : (<><FileArchive className="w-4 h-4 mr-2" />Generate PDF</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
